/**
 * Privé Customer Brain — Intelligence Collector
 * Sends anonymous visitor behavior events to prive-customer-brain.fly.dev
 *
 * Tracks: session context (referrer, UTM, landing), product views, dwell time,
 *         searches, similar clicks, WhatsApp taps, image searches,
 *         customer identity linking.
 */

(function() {
  'use strict';

  var API = 'https://prive-customer-brain.fly.dev';
  var SESSION_FLAG = 'prive_brain_session_started';
  var MAX_RETRIES = 2;
  var MAX_QUEUE_SIZE = 500;
  var PENDING_EVENTS_KEY = 'prive_brain_pending_events_v1';
  var ACTIVE_TAB_KEY_PREFIX = 'prive_brain_active_tab_v1:';
  var ACTIVE_TAB_TTL_MS = 45000;

  function _readToken() {
    var el = document.querySelector('meta[name="prive-intel-token"]');
    if (!el) return { ts: '', hmac: '', raw: '' };
    var raw = el.getAttribute('content') || '';
    var idx = raw.indexOf(':');
    if (idx === -1) return { ts: '', hmac: '', raw: '' };
    return { ts: raw.substring(0, idx), hmac: raw.substring(idx + 1), raw: raw };
  }

  var _auth = _readToken();

  // HMAC auth — read time-limited token injected by Liquid
  var _tsMeta = document.querySelector('meta[name="prive-ts"]');
  var _tokenMeta = document.querySelector('meta[name="prive-token"]');
  var AUTH_TS = _tsMeta ? _tsMeta.content : '';
  var AUTH_TOKEN = _tokenMeta ? _tokenMeta.content : '';

  var BrainIntel = {
    _queue: [],
    _flushTimer: null,
    _vid: null,
    _vidToken: null,
    _handoffTicket: null,
    _orderTicket: null,
    _handoffExpiresAt: null,
    _orderExpiresAt: null,
    _capabilityPromise: null,
    _refreshPromise: null,
    _capabilityEpoch: 0,
    _capabilityRecoveryTimer: null,
    _ticketRenewalTimer: null,
    _trackingStarted: false,
    _checkoutTrackingInstalled: false,
    _unloading: false,
    _inflightBatch: null,
    _rawFetch: null,
    _cartVidSyncing: false,
    _cartVidSyncPromise: null,
    _lastCartSnapshot: null,
    _identifyPromise: null,
    _identifyRetryTimer: null,
    _pendingEventsKey: null,
    _activeTabKey: null,

    init: function() {
      try { this._rawFetch = window.fetch ? window.fetch.bind(window) : null; } catch (_) { this._rawFetch = null; }
      var tabId = null;
      try { tabId = sessionStorage.getItem('prive_brain_tab_id'); } catch (_) {}
      if (tabId) {
        try {
          var activeAt = Number(localStorage.getItem(ACTIVE_TAB_KEY_PREFIX + tabId));
          if (activeAt && activeAt > Date.now() - ACTIVE_TAB_TTL_MS) tabId = null;
        } catch (_) {}
      }
      if (!tabId) {
        try { tabId = crypto.randomUUID(); } catch (_) { tabId = String(Date.now()) + '-' + Math.random().toString(36).slice(2); }
        try { sessionStorage.setItem('prive_brain_tab_id', tabId); } catch (_) {}
      }
      this._pendingEventsKey = PENDING_EVENTS_KEY + ':' + tabId;
      this._activeTabKey = ACTIVE_TAB_KEY_PREFIX + tabId;
      try { localStorage.setItem(this._activeTabKey, String(Date.now())); } catch (_) {}
      this._restoreQueue();
      this._trackCheckoutStart();
      var self = this;
      this._ensureCapability(0).then(function(ready) {
        if (ready) self._startTracking();
        else self._scheduleCapabilityRecovery();
      }).catch(function() {});
      window.addEventListener('pagehide', function() {
        self._unloading = true;
        self._flush();
        try { localStorage.removeItem(self._activeTabKey); } catch (_) {}
      });
      window.addEventListener('pageshow', function() {
        self._unloading = false;
        try { localStorage.setItem(self._activeTabKey, String(Date.now())); } catch (_) {}
        if (self._vidToken) self._flush();
      });
      setInterval(function() {
        try { localStorage.setItem(self._activeTabKey, String(Date.now())); } catch (_) {}
      }, 15000);
      window.addEventListener('online', function() {
        if (self._capabilityRecoveryTimer || !self._vidToken) self._scheduleCapabilityRecovery(0);
      });
      window.addEventListener('storage', function(event) {
        if (!event || event.key !== 'prive_brain_capability_v2' || !event.newValue) return;
        try {
          var capability = self._readStoredCapability();
          if (self._isCompleteCapability(capability)
            && (capability.vid !== self._vid || capability.vid_token !== self._vidToken)) {
            self._activateCapability(capability, false);
          }
        } catch (_) {}
      });
    },

    _startTracking: function() {
      if (this._trackingStarted) return;
      this._trackingStarted = true;
      this._maybeFireSessionStart();
      this._linkLoggedInCustomer();
      this._autoTrackProductView();
      this._autoTrackSearch();
      this._wrapFetchForCart();
      this._trackCartView();
      this._trackCheckoutStart();
    },

    _isCompleteCapability: function(capability) {
      return Boolean(capability
        && /^[A-Za-z0-9_-]{20,160}$/.test(capability.vid || '')
        && /^[a-f0-9]{64}$/i.test(capability.vid_token || '')
        && /^[A-Za-z0-9_-]{43}$/.test(capability.handoff_ticket || '')
        && /^[A-Za-z0-9_-]{43}$/.test(capability.order_ticket || '')
        && Number(capability.handoff_expires_at) > Date.now() + 60000
        && Number(capability.order_expires_at) > Date.now() + 60000);
    },

    _readStoredCapability: function() {
      var stores = [];
      try { stores.push(localStorage); } catch (_) {}
      try { stores.push(sessionStorage); } catch (_) {}
      for (var i = 0; i < stores.length; i++) {
        try {
          var stored = JSON.parse(stores[i].getItem('prive_brain_capability_v2') || 'null');
          if (this._isCompleteCapability(stored)) {
            return stored;
          }
        } catch (_) {}
      }
      return null;
    },

    _readStoredRenewalCapability: function() {
      var stores = [];
      try { stores.push(localStorage); } catch (_) {}
      try { stores.push(sessionStorage); } catch (_) {}
      for (var i = 0; i < stores.length; i++) {
        try {
          var stored = JSON.parse(stores[i].getItem('prive_brain_capability_v2') || 'null');
          if (stored
            && /^[A-Za-z0-9_-]{20,160}$/.test(stored.vid || '')
            && /^[a-f0-9]{64}$/i.test(stored.vid_token || '')) {
            return { vid: stored.vid, vid_token: stored.vid_token };
          }
        } catch (_) {}
      }
      return null;
    },

    _storeCapability: function(capability) {
      var serialized = JSON.stringify(capability);
      try {
        localStorage.setItem('prive_brain_capability_v2', serialized);
        return;
      } catch (_) {}
      try {
        sessionStorage.setItem('prive_brain_capability_v2', serialized);
      } catch (_) {}
    },

    _activateCapability: function(capability, shouldStore, skipCart) {
      this._vid = capability.vid;
      this._vidToken = capability.vid_token;
      this._handoffTicket = capability.handoff_ticket;
      this._orderTicket = capability.order_ticket;
      this._handoffExpiresAt = Number(capability.handoff_expires_at);
      this._orderExpiresAt = Number(capability.order_expires_at);
      this._capabilityEpoch++;
      this._queue.forEach(function(event) { event.vid = capability.vid; });
      this._persistQueue();
      if (shouldStore !== false) this._storeCapability(capability);
      if (!skipCart) this._persistVidToCart('capability');
      this._scheduleTicketRenewal();
      try {
        window.dispatchEvent(new CustomEvent('prive:brain-vid-ready', {
          detail: { vid: capability.vid }
        }));
      } catch (_) {}
    },

    _clearStoredCapability: function() {
      var stores = [];
      try { stores.push(localStorage); } catch (_) {}
      try { stores.push(sessionStorage); } catch (_) {}
      for (var i = 0; i < stores.length; i++) {
        try {
          stores[i].removeItem('prive_brain_capability_v2');
          stores[i].removeItem('prive_brain_vid_v2');
          stores[i].removeItem('prive_brain_vid_token_v2');
          stores[i].removeItem('prive_brain_vid');
        } catch (_) {}
      }
      this._vid = null;
      this._vidToken = null;
      this._handoffTicket = null;
      this._orderTicket = null;
      this._handoffExpiresAt = null;
      this._orderExpiresAt = null;
    },

    _ensureCapability: function(attempt, force, renewal) {
      if (this._capabilityPromise) return this._capabilityPromise;
      var self = this;
      var request = this._requestCapability(attempt, force, renewal);
      this._capabilityPromise = request.then(function(ready) {
        self._capabilityPromise = null;
        return ready;
      }, function(err) {
        self._capabilityPromise = null;
        throw err;
      });
      return this._capabilityPromise;
    },

    _requestCapability: function(attempt, force, renewal) {
      var self = this;
      var run = this._withCrossTabLock('prive-brain-capability-issuance', function() {
        var stored = self._readStoredCapability();
        if (stored) {
          var alreadyRenewed = force && renewal
            && (stored.vid !== renewal.vid || stored.vid_token !== renewal.vid_token);
          if (!force || !renewal || alreadyRenewed) {
            self._activateCapability(stored);
            return true;
          }
        }
        var requestRenewal = renewal;
        if (!force && !requestRenewal) requestRenewal = self._readStoredRenewalCapability();
        if (!self._rawFetch) return false;
        var requestEpoch = self._capabilityEpoch;
        return self._rawFetch(API + '/api/intelligence/session', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Prive-TS': AUTH_TS, 'X-Prive-Token': AUTH_TOKEN },
          body: JSON.stringify({
            ts: AUTH_TS,
            token: AUTH_TOKEN,
            renew_vid: requestRenewal && requestRenewal.vid,
            renew_vid_token: requestRenewal && requestRenewal.vid_token
          }),
          keepalive: true
        }).then(function(resp) {
          if (!resp || !resp.ok) throw new Error('capability request failed');
          return resp.json();
        }).then(function(capability) {
          if (!self._isCompleteCapability(capability)
            || (requestRenewal && capability.vid !== requestRenewal.vid)) {
            throw new Error('invalid capability response');
          }
          if (self._capabilityEpoch !== requestEpoch) {
            var authority = self._readStoredCapability();
            if (authority) self._activateCapability(authority, false);
            return Boolean(self._vid && self._vidToken);
          }
          self._activateCapability(capability);
          return true;
        });
      });
      return run.catch(function() { return false; }).then(function(ready) {
        if (ready || attempt >= MAX_RETRIES) return ready;
        return new Promise(function(resolve) {
          setTimeout(function() {
            resolve(self._requestCapability(attempt + 1, force, renewal));
          }, 1000 * (attempt + 1));
        });
      });
    },

    _refreshCapability: function(sentVid, sentToken, sentEpoch) {
      if (this._refreshPromise) return this._refreshPromise;
      if (this._vid !== sentVid || this._vidToken !== sentToken || this._capabilityEpoch !== sentEpoch) {
        return Promise.resolve(Boolean(this._vid && this._vidToken));
      }
      var self = this;
      var request = this._ensureCapability(0, true, {
        vid: sentVid,
        vid_token: sentToken
      });
      this._refreshPromise = request.then(function(ready) {
        self._refreshPromise = null;
        return ready;
      }, function(err) {
        self._refreshPromise = null;
        throw err;
      });
      return this._refreshPromise;
    },

    ensureFreshPurposeTickets: function() {
      if (this._handoffTicket && this._orderTicket
        && this._handoffExpiresAt > Date.now() + 60000
        && this._orderExpiresAt > Date.now() + 60000) {
        return Promise.resolve(true);
      }
      if (!this._vid || !this._vidToken) return this._ensureCapability(0);
      return this._refreshCapability(this._vid, this._vidToken, this._capabilityEpoch);
    },

    _scheduleTicketRenewal: function() {
      if (this._ticketRenewalTimer) clearTimeout(this._ticketRenewalTimer);
      var renewAt = Math.min(this._handoffExpiresAt || Infinity, this._orderExpiresAt || Infinity) - Date.now() - 5 * 60 * 1000;
      if (!Number.isFinite(renewAt)) return;
      var self = this;
      this._ticketRenewalTimer = setTimeout(function() {
        self._ticketRenewalTimer = null;
        self.ensureFreshPurposeTickets().then(function(ready) {
          if (!ready) self._scheduleCapabilityRecovery();
        }).catch(function() { self._scheduleCapabilityRecovery(); });
      }, Math.max(1000, renewAt));
    },

    _scheduleCapabilityRecovery: function(delay) {
      if (this._capabilityRecoveryTimer) {
        if (delay !== 0) return;
        clearTimeout(this._capabilityRecoveryTimer);
        this._capabilityRecoveryTimer = null;
      }
      var self = this;
      this._capabilityRecoveryTimer = setTimeout(function() {
        self._capabilityRecoveryTimer = null;
        var renewal = self._vid && self._vidToken
          ? { vid: self._vid, vid_token: self._vidToken }
          : null;
        self._ensureCapability(0, true, renewal).then(function(ready) {
          if (ready) {
            self._startTracking();
            self._flush();
          } else self._scheduleCapabilityRecovery();
        }).catch(function() { self._scheduleCapabilityRecovery(); });
      }, delay == null ? 60000 : delay);
    },

    _maybeFireSessionStart: function() {
      var already = false;
      try { already = sessionStorage.getItem(SESSION_FLAG) === '1'; } catch (e) {}
      if (already) return;

      var data = {};
      try { data.landing_path = window.location.pathname; } catch (e) {}
      try {
        var ref = document.referrer || '';
        if (ref) {
          try { var u = new URL(ref); data.referrer = u.origin + u.pathname; } catch (_) { data.referrer = null; }
        } else { data.referrer = null; }
      } catch (e) {}

      // UTM params (ad-source attribution)
      try {
        var params = new URLSearchParams(window.location.search);
        ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach(function(k) {
          var v = params.get(k);
          if (v) data[k] = v.slice(0, 120);
        });
      } catch (e) {}

      // Device / viewport — captured once, cheap signal for segmentation
      try {
        if (window.screen) {
          data.screen_w = window.screen.width;
          data.screen_h = window.screen.height;
        }
        data.viewport_w = window.innerWidth;
        data.viewport_h = window.innerHeight;
      } catch (e) {}

      try { data.language = navigator.language || null; } catch (e) {}
      try { data.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch (e) {}

      this._track('session_start', data);
      try { sessionStorage.setItem(SESSION_FLAG, '1'); } catch (e) {}
    },

    _linkLoggedInCustomer: function() {
      try {
        var cidMeta = document.querySelector('meta[name="prive-cid"]');
        var tsMeta = document.querySelector('meta[name="prive-live-search-ts"]');
        var tokenMeta = document.querySelector('meta[name="prive-live-search-token"]');
        var cid = cidMeta && cidMeta.content;
        var customerTs = tsMeta && tsMeta.content;
        var customerToken = tokenMeta && tokenMeta.content;
        if (cid && customerTs && customerToken) {
          this._maybeIdentify(String(cid), String(customerTs), String(customerToken));
        }
      } catch (_) {}
    },

    _restoreQueue: function() {
      var self = this;
      return this._withCrossTabLock('prive-brain-queue-recovery', function() {
        var restored = [];
        var claimedKeys = [];
        try {
          var keys = [];
          for (var i = 0; i < localStorage.length; i++) {
            var key = localStorage.key(i);
            if (key === PENDING_EVENTS_KEY || (key && key.indexOf(PENDING_EVENTS_KEY + ':') === 0)) keys.push(key);
          }
          keys.forEach(function(key) {
            try {
              var owner = key.indexOf(PENDING_EVENTS_KEY + ':') === 0
                ? key.slice((PENDING_EVENTS_KEY + ':').length)
                : '';
              var activeAt = owner ? Number(localStorage.getItem(ACTIVE_TAB_KEY_PREFIX + owner)) : 0;
              var isOwn = key === self._pendingEventsKey;
              if (!isOwn && activeAt && activeAt > Date.now() - ACTIVE_TAB_TTL_MS) return;
              var events = JSON.parse(localStorage.getItem(key) || 'null');
              if (Array.isArray(events)) restored = restored.concat(events);
              claimedKeys.push(key);
              if (owner && !isOwn) localStorage.removeItem(ACTIVE_TAB_KEY_PREFIX + owner);
            } catch (_) {}
          });
          claimedKeys.forEach(function(key) { localStorage.removeItem(key); });
        } catch (_) {}
        if (restored.length === 0) {
          try {
            var fallback = JSON.parse(sessionStorage.getItem(self._pendingEventsKey) || 'null');
            if (Array.isArray(fallback)) restored = fallback;
          } catch (_) {}
        }
        if (restored.length === 0) return false;
        self._queue = restored.filter(function(event) {
          return event && typeof event.e === 'string' && Number.isFinite(Number(event.t));
        }).slice(-MAX_QUEUE_SIZE);
        self._persistQueue();
        return true;
      });
    },

    _persistQueue: function() {
      var durable = (this._inflightBatch || []).concat(this._queue).slice(0, MAX_QUEUE_SIZE);
      var key = this._pendingEventsKey || PENDING_EVENTS_KEY;
      if (durable.length === 0) {
        try { localStorage.removeItem(key); } catch (_) {}
        try { sessionStorage.removeItem(key); } catch (_) {}
        return;
      }
      var serialized = JSON.stringify(durable);
      try { localStorage.setItem(key, serialized); return; } catch (_) {}
      try { sessionStorage.setItem(key, serialized); } catch (_) {}
    },

    _track: function(event, data) {
      var available = MAX_QUEUE_SIZE - (this._inflightBatch ? this._inflightBatch.length : 0);
      if (available <= 0) return;
      if (this._queue.length >= available) this._queue.shift();
      this._queue.push({ e: event, d: data || {}, t: Date.now(), vid: this._vid });
      this._persistQueue();
      var self = this;
      if (this._queue.length >= 5) {
        this._flush();
      } else if (!this._flushTimer) {
        this._flushTimer = setTimeout(function() { self._flush(); }, 3000);
      }
    },

    _requeueBatch: function(batch) {
      var currentVid = this._vid;
      batch.forEach(function(event) { event.vid = currentVid; });
      if (this._inflightBatch === batch) this._inflightBatch = null;
      this._queue = batch.concat(this._queue);
      if (this._queue.length > MAX_QUEUE_SIZE) {
        this._queue.splice(0, this._queue.length - MAX_QUEUE_SIZE);
      }
      this._persistQueue();
    },

    _scheduleFlushRetry: function(attempt) {
      var self = this;
      var delay = attempt < MAX_RETRIES ? 5000 : 60000;
      this._flushTimer = setTimeout(function() { self._flush(attempt + 1); }, delay);
    },

    _flush: function(retryCount) {
      if (this._flushTimer) { clearTimeout(this._flushTimer); this._flushTimer = null; }
      if (this._inflightBatch) { this._persistQueue(); return; }
      if (this._queue.length === 0) return;
      if (!this._vid || !this._vidToken) return;
      var batch = this._queue.splice(0);
      var sentVid = this._vid;
      var sentToken = this._vidToken;
      var sentEpoch = this._capabilityEpoch;
      batch.forEach(function(event) { event.vid = sentVid; });
      this._inflightBatch = batch;
      this._persistQueue();
      var payload = { events: batch, vid: sentVid, vid_token: sentToken, ts: AUTH_TS, token: AUTH_TOKEN };
      var body = JSON.stringify(payload);
      var url = API + '/api/intelligence';
      // During unload there is no acknowledgable response. Keep the bounded batch
      // durably queued and retry after BFCache restore or the next page load.
      if (this._unloading) {
        this._requeueBatch(batch);
        return;
      }
      var self = this;
        var attempt = retryCount || 0;
        var headers = {
          'Content-Type': 'application/json',
          'X-Prive-TS': AUTH_TS,
          'X-Prive-Token': AUTH_TOKEN,
          'X-Prive-VID-Token': sentToken
        };
        fetch(url, {
          method: 'POST',
          headers: headers,
          body: body,
          keepalive: true
        }).then(function(resp) {
          var refresh = resp.headers && resp.headers.get
            && resp.headers.get('X-Prive-Capability') === 'refresh';
          if (refresh) {
            self._requeueBatch(batch);
            if (attempt < MAX_RETRIES) {
              self._refreshCapability(sentVid, sentToken, sentEpoch).then(function(ready) {
                if (ready) self._flush(attempt + 1);
                else self._scheduleCapabilityRecovery();
              }).catch(function() { self._scheduleCapabilityRecovery(); });
            } else self._scheduleCapabilityRecovery();
            return;
          }
          if (resp.status === 401) {
            self._requeueBatch(batch);
            if (attempt < MAX_RETRIES) {
              self._refreshCapability(sentVid, sentToken, sentEpoch).then(function(ready) {
                if (ready) self._flush(attempt + 1);
                else self._scheduleCapabilityRecovery();
              }).catch(function() { self._scheduleCapabilityRecovery(); });
            } else self._scheduleCapabilityRecovery();
          } else if (resp.status === 429 || resp.status >= 500) {
            self._requeueBatch(batch);
            self._scheduleFlushRetry(attempt);
          } else {
            if (self._inflightBatch === batch) self._inflightBatch = null;
            self._persistQueue();
            if (self._queue.length) self._flush();
          }
        }).catch(function() {
          self._requeueBatch(batch);
          self._scheduleFlushRetry(attempt);
        });
    },

    _autoTrackProductView: function() {
      var meta = document.querySelector('meta[property="og:type"][content="product"]');
      if (!meta) return;
      var handle = window.location.pathname.split('/products/')[1];
      if (!handle) return;
      handle = handle.split('?')[0].split('#')[0];
      var data = { handle: handle };
      // Per-field extraction: JSON-LD for title/vendor, DOM for price (reflects
      // the currently selected variant), with cross-fallback for missing fields.
      var ld = this._productJsonLd();
      var titleEl = document.querySelector('.product-meta__title');
      var vendorEl = document.querySelector('.product-meta__vendor');
      var priceEl = document.querySelector('.price--large');
      // Title: JSON-LD first, DOM fallback
      if (ld && ld.name) { data.title = ld.name; }
      else if (titleEl) { data.title = titleEl.textContent.trim(); }
      // Vendor: JSON-LD first, DOM fallback
      if (ld && ld.brand && ld.brand.name) { data.vendor = ld.brand.name; }
      else if (vendorEl) { data.vendor = vendorEl.textContent.trim(); }
      // Price: DOM first (shows the selected variant's rendered price),
      // JSON-LD fallback (offers[0] may not match the active variant).
      if (priceEl) {
        var dp = parseFloat(priceEl.textContent.replace(/[^\d.,]/g, '').replace(',', ''));
        if (!isNaN(dp)) data.price = dp;
      }
      if (data.price == null && ld && ld.offers) {
        var offerPrice = Array.isArray(ld.offers) ? ld.offers[0] && ld.offers[0].price : ld.offers.price;
        if (offerPrice != null) { var lp = parseFloat(offerPrice); if (!isNaN(lp)) data.price = lp; }
      }
      this._track('product_view', data);
      var dwellData = { handle: handle };
      if (data.title) dwellData.title = data.title;
      if (data.vendor) dwellData.vendor = data.vendor;
      if (data.price != null) dwellData.price = data.price;
      // Dwell time tracking
      var self = this;
      var start = Date.now();
      var d30 = false, d60 = false;
      var iv = setInterval(function() {
        if (document.hidden) return;
        var s = Math.floor((Date.now() - start) / 1000);
        if (s >= 30 && !d30) { d30 = true; self._track('product_dwell', Object.assign({ seconds: 30 }, dwellData)); }
        if (s >= 60 && !d60) { d60 = true; self._track('product_dwell', Object.assign({ seconds: 60 }, dwellData)); clearInterval(iv); }
      }, 10000);
      window.addEventListener('pagehide', function() { clearInterval(iv); }, { once: true });
    },

    _autoTrackSearch: function() {
      var self = this;
      var last = '';
      var debounce = null;
      document.addEventListener('input', function(e) {
        if (!e.target || !e.target.matches || !e.target.matches('.predictive-search__input')) return;
        var q = e.target.value.trim();
        if (q.length >= 3 && q !== last) {
          last = q;
          clearTimeout(debounce);
          debounce = setTimeout(function() { self._track('search', { query: q }); }, 400);
        }
      }, true);
    },

    _cartSnapshotData: function(cart) {
      if (!cart || typeof cart !== 'object') return {};
      var data = {};
      if (cart.token) data.cart_token = String(cart.token);
      if (typeof cart.item_count !== 'undefined') data.cart_item_count = cart.item_count;
      if (typeof cart.total_price !== 'undefined') data.cart_total_price = cart.total_price;
      if (cart.currency) data.currency = cart.currency;
      try {
        var attrs = cart.attributes || {};
        if (attrs && attrs._prive_vid) data.cart_prive_vid = String(attrs._prive_vid);
      } catch (_) {}
      try {
        if (cart.items && cart.items.length) {
          data.cart_lines = cart.items.slice(0, 12).map(function(item) {
            var line = {};
            if (item.key) line.key = String(item.key);
            if (item.id) line.variant_id = String(item.id);
            if (item.variant_id) line.variant_id = String(item.variant_id);
            if (item.product_id) line.product_id = String(item.product_id);
            if (item.handle) line.handle = String(item.handle);
            if (item.url) line.url = String(item.url).split('?')[0];
            if (item.title) line.title = String(item.title).slice(0, 180);
            if (item.vendor) line.vendor = String(item.vendor).slice(0, 120);
            if (typeof item.quantity !== 'undefined') line.quantity = item.quantity;
            if (typeof item.price !== 'undefined') line.price = item.price;
            if (typeof item.final_price !== 'undefined') line.final_price = item.final_price;
            if (typeof item.line_price !== 'undefined') line.line_price = item.line_price;
            return line;
          });
        }
      } catch (_) {}
      return data;
    },

    _readCartSnapshot: function(keepalive) {
      if (!this._rawFetch) return Promise.resolve(null);
      var self = this;
      try {
        return this._rawFetch('/cart.js', {
          method: 'GET',
          headers: { 'Accept': 'application/json' },
          credentials: 'same-origin',
          keepalive: !!keepalive
        }).then(function(resp) {
          if (!resp || !resp.ok) return null;
          return resp.json();
        }).then(function(cart) {
          if (cart) self._lastCartSnapshot = cart;
          return cart || null;
        }).catch(function() { return null; });
      } catch (_) {
        return Promise.resolve(null);
      }
    },

    _hydrateWithCartSnapshot: function(data, keepalive) {
      var self = this;
      var base = data || {};
      return this._readCartSnapshot(keepalive).then(function(cart) {
        var snapshot = self._cartSnapshotData(cart || self._lastCartSnapshot);
        Object.keys(snapshot).forEach(function(k) { base[k] = snapshot[k]; });
        return base;
      });
    },

    _writeCartCapability: function(attributes, isCheckout, attempt) {
      var self = this;
      var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
      var timeout = controller ? setTimeout(function() { controller.abort(); }, 5000) : null;
      var request;
      try {
        request = this._rawFetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ attributes: attributes }),
          credentials: 'same-origin',
          keepalive: isCheckout,
          signal: controller ? controller.signal : undefined
        }).then(function(resp) {
          return Boolean(resp && resp.ok);
        }).catch(function() { return false; });
      } catch (_) {
        request = Promise.resolve(false);
      }
      return request.then(function(ok) {
        if (timeout) clearTimeout(timeout);
        if (!ok && attempt < MAX_RETRIES) {
          return self._writeCartCapability(attributes, isCheckout, attempt + 1);
        }
        return ok;
      });
    },

    _withCrossTabLock: function(name, operation) {
      try {
        if (navigator.locks && typeof navigator.locks.request === 'function') {
          return navigator.locks.request(name, { mode: 'exclusive' }, operation);
        }
      } catch (_) {}
      try { return Promise.resolve(operation()); } catch (err) { return Promise.reject(err); }
    },

    _persistVidToCart: function(reason) {
      if (!this._rawFetch) return Promise.resolve(false);
      var self = this;
      if (!this._orderTicket || this._orderExpiresAt <= Date.now() + 60000) {
        return this.ensureFreshPurposeTickets().then(function(ready) {
          return ready ? self._persistVidToCart(reason) : false;
        }).catch(function() { return false; });
      }
      if (this._cartVidSyncPromise) {
        return this._cartVidSyncPromise.then(function() {
          return self._persistVidToCart(reason);
        });
      }

      this._cartVidSyncing = true;
      var isCheckout = reason === 'checkout';
      var targetTicket = null;
      var request = this._withCrossTabLock('prive-brain-cart-capability', function() {
        var authority = self._readStoredCapability();
        if (authority && (authority.vid !== self._vid || authority.vid_token !== self._vidToken)) {
          self._activateCapability(authority, false, true);
        }
        targetTicket = self._orderTicket;
        if (!targetTicket || self._orderExpiresAt <= Date.now() + 60000) return false;
        if (!isCheckout) {
          try {
            if (sessionStorage.getItem('prive_brain_cart_order_ticket') === targetTicket) return true;
          } catch (_) {}
        }
        return self._writeCartCapability({
          _prive_order_ticket: targetTicket,
          _prive_vid: '',
          _prive_vid_token: ''
        }, isCheckout, 0);
      });

      this._cartVidSyncPromise = request.then(function(ok) {
        if (ok) {
          try { sessionStorage.setItem('prive_brain_cart_order_ticket', targetTicket); } catch (_) {}
        }
        self._cartVidSyncPromise = null;
        self._cartVidSyncing = false;
        if (self._orderTicket !== targetTicket) {
          return self._persistVidToCart(reason).then(function(currentOk) {
            return ok && currentOk;
          });
        }
        return ok;
      });
      return this._cartVidSyncPromise;
    },

    _wrapFetchForCart: function() {
      if (!window.fetch || window.__priveBrainFetchWrapped) return;
      window.__priveBrainFetchWrapped = true;
      var self = this;
      var origFetch = window.fetch.bind(window);
      window.fetch = function(input, init) {
        var url = '';
        try { url = typeof input === 'string' ? input : (input && input.url) || ''; } catch (_) {}
        var p = origFetch(input, init);
        if (!url || url.indexOf('/cart/') === -1) return p;
        return p.then(function(resp) {
          try {
            if (!resp || !resp.ok) return resp;
            var evt = null;
            if (url.indexOf('/cart/add') !== -1) evt = 'cart_add';
            else if (url.indexOf('/cart/change') !== -1) evt = 'cart_change';
            else if (url.indexOf('/cart/update') !== -1) evt = 'cart_update';
            else if (url.indexOf('/cart/clear') !== -1) evt = 'cart_clear';
            if (!evt) return resp;
            var data = {};
            try {
              var body = init && init.body;
              if (body && typeof body === 'string') {
                try {
                  var parsed = JSON.parse(body);
                  if (parsed && parsed.id) data.variant_id = String(parsed.id);
                  if (parsed && typeof parsed.quantity !== 'undefined') { var pq = parseInt(parsed.quantity, 10); if (!isNaN(pq)) data.quantity = pq; }
                } catch (_) {
                  try {
                    var fp = new URLSearchParams(body);
                    var id = fp.get('id'); if (id) data.variant_id = id;
                    var qty = fp.get('quantity'); if (qty !== null) { var uq = parseInt(qty, 10); if (!isNaN(uq)) data.quantity = uq; }
                  } catch (__) {}
                }
              } else if (body && typeof FormData !== 'undefined' && body instanceof FormData) {
                var fid = body.get('id'); if (fid) data.variant_id = String(fid);
                var fqty = body.get('quantity'); if (fqty !== null) { var fdq = parseInt(fqty, 10); if (!isNaN(fdq)) data.quantity = fdq; }
              }
            } catch (_) {}
            try { data.path = window.location.pathname; } catch (_) {}
            // Queue the base event synchronously so a navigation right after the
            // cart write (e.g. cartType="page" redirects to /cart) cannot drop it.
            var cached = self._cartSnapshotData(self._lastCartSnapshot);
            Object.keys(cached).forEach(function(k) { if (data[k] == null) data[k] = cached[k]; });
            self._track(evt, data);
            if (evt === 'cart_add' || evt === 'cart_change' || evt === 'cart_update') self._persistVidToCart(evt);
            // Enrich with a fresh /cart.js snapshot in the background.
            self._hydrateWithCartSnapshot({ path: data.path }, false).then(function(payload) {
              if (payload.cart_token || payload.cart_item_count || payload.cart_lines) self._track('cart_snapshot', payload);
            }).catch(function() {});
          } catch (_) {}
          return resp;
        }).catch(function(err) { throw err; });
      };
    },

    _trackCartView: function() {
      try {
        var path = window.location.pathname || '';
        if (path === '/cart' || path.indexOf('/cart') === 0) {
          var data = { path: path };
          var cached = this._cartSnapshotData(this._lastCartSnapshot);
          Object.keys(cached).forEach(function(k) { data[k] = cached[k]; });
          this._track('cart_view', data);
          var self = this;
          this._hydrateWithCartSnapshot({ path: path }, false).then(function(payload) {
            if (payload.cart_token || payload.cart_item_count || payload.cart_lines) self._track('cart_snapshot', payload);
          });
        }
      } catch (_) {}
    },

    _trackCheckoutStart: function() {
      if (this._checkoutTrackingInstalled) return;
      this._checkoutTrackingInstalled = true;
      var self = this;
      var inFlight = false;
      var resuming = false;
      var fenceCheckout = function(e, hit) {
        if (resuming) {
          resuming = false;
          inFlight = false;
          return;
        }
        if (e.preventDefault) e.preventDefault();
        if (e.stopImmediatePropagation) e.stopImmediatePropagation();
        if (inFlight) return;
        inFlight = true;
        var data = {};
        try { data.path = window.location.pathname; } catch (_) {}
        try {
          if (hit.tagName === 'A' && hit.href) data.href = hit.href;
        } catch (_) {}
        var cached = self._cartSnapshotData(self._lastCartSnapshot);
        Object.keys(cached).forEach(function(k) { data[k] = cached[k]; });
        self._track('checkout_start', data);
        self._flush();
        if (!data.cart_token && !data.cart_lines) {
          self._hydrateWithCartSnapshot({ path: data.path, href: data.href }, true).then(function(payload) {
            if (payload.cart_token || payload.cart_item_count || payload.cart_lines) {
              self._track('checkout_cart_snapshot', payload);
              self._flush();
            }
          });
        }
        var resumeCheckout = function() {
          try {
            if (hit.tagName === 'A' && hit.href) {
              inFlight = false;
              window.location.assign(hit.href);
              return;
            }
            var form = hit.tagName === 'FORM' ? hit : (hit.form || (hit.closest && hit.closest('form')));
            if (form) {
              resuming = true;
              inFlight = false;
              if (form.requestSubmit) form.requestSubmit(hit.tagName === 'FORM' ? undefined : hit);
              else form.submit();
              Promise.resolve().then(function() { resuming = false; });
              return;
            }
            if (hit.click) {
              resuming = true;
              inFlight = false;
              hit.click();
              Promise.resolve().then(function() { resuming = false; });
            }
          } catch (_) {
            resuming = false;
            inFlight = false;
          }
        };
        self._persistVidToCart('checkout').then(function(ok) {
          if (ok) {
            resumeCheckout();
            return;
          }
          inFlight = false;
          try {
            window.dispatchEvent(new CustomEvent('prive:brain-checkout-blocked', {
              detail: { reason: 'capability_persistence_failed' }
            }));
          } catch (_) {}
        }).catch(function() { inFlight = false; });
      };
      document.addEventListener('click', function(e) {
        var el = e.target;
        if (!el || !el.closest) return;
        var hit = el.closest('a[href*="/checkout"], button[name="checkout"], input[name="checkout"], [data-prive-checkout], shopify-accelerated-checkout, shopify-accelerated-checkout-cart, .shopify-payment-button__button');
        if (hit) fenceCheckout(e, hit);
      }, true);
      document.addEventListener('submit', function(e) {
        var form = e.target;
        if (form) fenceCheckout(e, form);
      }, true);
    },

    _productJsonLd: function() {
      try {
        var scripts = document.querySelectorAll('script[type="application/ld+json"]');
        for (var i = 0; i < scripts.length; i++) {
          var obj = JSON.parse(scripts[i].textContent);
          if (obj && obj['@type'] === 'Product') return obj;
        }
      } catch (e) {}
      return null;
    },

    trackSimilarClick: function(handle, from) { this._track('similar_click', { handle: handle, from: from }); },
    trackWhatsAppTap: function(handle, title, vendor, price) {
      var data = { handle: handle };
      if (title) data.title = title;
      if (vendor) data.vendor = vendor;
      if (price != null) data.price = price;
      this._track('whatsapp_tap', data);
    },
    trackImageSearch: function(payload) {
      var data = payload && typeof payload === 'object' ? payload : {};
      this._track('image_search', data);
    },
    trackImageSearchSelect: function(payload) {
      var data = payload && typeof payload === 'object' ? payload : {};
      this._track('image_search_select', data);
    },
    linkCustomer: function(cid) { return this._maybeIdentify(cid); },
    _maybeIdentify: function(cid, customerTs, customerToken) {
      if (!cid || !this._vid || !this._vidToken || !this._rawFetch) return Promise.resolve(false);
      var sid = String(cid);
      var dedupeKey = this._vid + ':' + sid;
      var stored = null;
      try { stored = localStorage.getItem('prive_brain_cid_verified_v2'); } catch(e) {}
      if (stored === dedupeKey) return Promise.resolve(true);
      var sessionEmit = false;
      try { sessionEmit = sessionStorage.getItem('prive_brain_cid_verified_v2_emitted') === dedupeKey; } catch(e) {}
      if (sessionEmit) return Promise.resolve(true);
      if (!customerTs || !customerToken) {
        try {
          var tsMeta = document.querySelector('meta[name="prive-live-search-ts"]');
          var tokenMeta = document.querySelector('meta[name="prive-live-search-token"]');
          customerTs = tsMeta && tsMeta.content;
          customerToken = tokenMeta && tokenMeta.content;
        } catch (_) {}
      }
      if (!customerTs || !customerToken) return Promise.resolve(false);
      if (this._identifyPromise) return this._identifyPromise;

      var self = this;
      var retryable = false;
      var staleGeneration = false;
      var sentVid = this._vid;
      var sentToken = this._vidToken;
      var sentEpoch = this._capabilityEpoch;
      this._identifyPromise = this._rawFetch(API + '/api/intelligence/identify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Prive-VID-Token': sentToken },
        body: JSON.stringify({
          vid: sentVid,
          vid_token: sentToken,
          customer_id: sid,
          customer_ts: String(customerTs),
          customer_token: String(customerToken)
        }),
        keepalive: true
      }).then(function(resp) {
        retryable = !resp || resp.status === 429 || resp.status >= 500;
        if (!resp || !resp.ok) return false;
        if (self._vid !== sentVid || self._vidToken !== sentToken || self._capabilityEpoch !== sentEpoch) {
          staleGeneration = true;
          return false;
        }
        try { localStorage.setItem('prive_brain_cid_verified_v2', dedupeKey); } catch(e) {}
        try { sessionStorage.setItem('prive_brain_cid_verified_v2_emitted', dedupeKey); } catch(e) {}
        self._track('identify', { customerId: sid, verified: true });
        return true;
      }).catch(function() {
        retryable = true;
        return false;
      }).then(function(linked) {
        self._identifyPromise = null;
        if (staleGeneration) {
          Promise.resolve().then(function() {
            self._maybeIdentify(sid, customerTs, customerToken);
          });
          return false;
        }
        if (!linked && retryable && !self._identifyRetryTimer) {
          self._identifyRetryTimer = setTimeout(function() {
            self._identifyRetryTimer = null;
            self._maybeIdentify(sid, customerTs, customerToken);
          }, 60000);
        }
        return linked;
      });
      return this._identifyPromise;
    }
  };

  BrainIntel.init();
  window.PriveBrainIntel = BrainIntel;

})();
